/**
 * Spaces API - Unified leader-config settings write (request / response)
 *
 * PATCH /v1/spaces/:spaceId/settings
 *
 * The unified leader-config write surface for ALL space kinds (section +
 * community). It is the MANAGE-authorized sibling of the self-authorized
 * `PUT /v1/spaces/:spaceId/preferences` route (plan KTD1): this body carries the
 * shared editable space config (identity, design, visibility, the seven feature
 * toggles), and the server resolves the space kind from `spaceId` then branches
 * internally to the existing section-update / group-update services (plan KTD2).
 * Web admin migrates onto this route next (plan U7), replacing its client-side
 * per-kind branching (`space-write-adapter.ts`).
 *
 * `.strict()` is LOAD-BEARING: an unknown key is REJECTED with a 400 at the
 * contract boundary, never silently dropped or forwarded to a delegated service.
 * `defaultNotifyLevel` is a declared optional field (U18: now a REAL write that
 * persists to Section.defaultNotifyLevel for sections and UserGroup.defaultNotifyLevel
 * for communities). The space `kind` is NOT a body field: it is resolved server-side
 * from `spaceId`, so a client cannot select the weaker authorization branch.
 *
 * PATCH semantics: every field is OPTIONAL and an absent field leaves the
 * persisted value untouched. `null` clears a nullable field. At least one field
 * must be present (an empty body has nothing to update). The server maps each
 * field to the per-kind service the way the web adapter encodes it:
 *   - `description`  → community `description` ONLY. For a SECTION the server
 *                      REJECTS the field (400): the description is synced from
 *                      the SIS and cannot be pushed back to Blackbaud, so it is
 *                      not locally editable.
 *   - `displayName`  → section `displayName` override / community BOTH `name` +
 *                      `displayName`.
 *   - `bannerBackgroundColor` clear asymmetry → section `null` clears / community
 *                      empty-string clears.
 *   - the seven toggles pass through identically.
 */

import { z } from 'zod'
import { GroupDesignThemeSchema } from '../groups/common'
import { SpaceDetailResponseSchema } from './detail'
import { UpdateSpaceSectionSettingsSchema } from './settings-update'
import { AppVisibilitySchema } from './common'

// ============================================================================
// PATCH /v1/spaces/:spaceId/settings
// ============================================================================

/**
 * The unified leader-config body. Identity + design + visibility fields, MERGED
 * with the seven shared feature toggles (`UpdateSpaceSectionSettingsSchema` —
 * the same toggle set the section-update endpoint accepts, reused here so the
 * toggle contract stays single-sourced).
 *
 * `.strict()` rejects any unknown key (notably a client-supplied `kind`) with a
 * 400 — the route never silently drops or forwards an unexpected field.
 * `.refine` rejects an empty body.
 */
export const UpdateSpaceSettingsRequestSchema = UpdateSpaceSectionSettingsSchema.extend({
  // Identity (shared-core edit fields).
  displayName: z.string().describe("The space's editable display name (override for sections).").optional(),
  // Community-only: the server rejects `description` for sections (SIS-synced).
  description: z.string().describe("The space's editable free-text description (community spaces only).").optional(),
  // Design fields. `designTheme` null clears (inheritance fallthrough);
  // `bannerBackgroundColor` null is an explicit clear, mapped per-kind by the
  // server (section null / community empty string).
  designTheme: GroupDesignThemeSchema.nullable()
    .describe('Design theme (light/dark variants). Null clears for inheritance fallthrough.')
    .optional(),
  bannerBackgroundColor: z.string().nullable().describe('Legacy banner background color. Null clears.').optional(),
  bannerLogoUrl: z.string().nullable().describe('Banner logo URL (CDN-hosted). Null clears.').optional(),
  bannerImageUrl: z.string().nullable().describe('Banner background image URL (CDN-hosted). Null clears.').optional(),
  avatarImageUrl: z.string().nullable().describe('Avatar image URL (CDN-hosted). Null clears.').optional(),
  // Visibility (identically named on both write endpoints; absent leaves untouched).
  showInApp: z.boolean().describe('Whether the space is visible in the mobile app.').optional(),
  betaTestersOnly: z.boolean().describe('Restrict visibility to beta testers.').optional(),
  appVisibility: AppVisibilitySchema.describe(
    '3-state visibility override (DEFAULT/SHOWN/HIDDEN). Takes precedence over showInApp when both are present.'
  ).optional(),
  // Section-only fields that pass through to the section-update delegated use-case.
  // Included here so the `.strict()` contract accepts them from clients that send
  // them via the unified settings route (e.g. web admin migrated in U7).
  autoPublishEnabled: z.boolean().describe('Auto-publish new content items.').optional(),
  autoPublishNews: z.boolean().describe('Auto-publish news items.').optional(),
  autoPublishAnnouncements: z.boolean().describe('Auto-publish announcements.').optional(),
  autoPublishEvents: z.boolean().describe('Auto-publish events.').optional(),
  // U18: tightened from z.string() to the canonical enum so the contract validates
  // values before they reach the delegated use-cases. Both section + community
  // branches now persist this field (Section.defaultNotifyLevel /
  // UserGroup.defaultNotifyLevel).
  defaultNotifyLevel: z
    .enum(['NONE', 'MENTIONS', 'IMPORTANT', 'ALL'])
    .describe('Default notification level new members inherit when joining this space.')
    .optional()
})
  .strict()
  .refine(body => Object.values(body).some(v => v !== undefined), {
    message: 'At least one editable field must be provided.'
  })

export type UpdateSpaceSettingsRequest = z.infer<typeof UpdateSpaceSettingsRequestSchema>

/**
 * Response echoed back after a successful settings write. The route re-reads and
 * returns the full updated `SpaceDetail` (same envelope as `GET /v1/spaces/:id`),
 * so the response schema IS `SpaceDetailResponseSchema` — re-exported here under a
 * write-specific name so the web admin mutation can Zod-parse the PATCH response
 * the same way the detail fetch parses its read.
 */
export const UpdateSpaceSettingsResponseSchema = SpaceDetailResponseSchema

export type UpdateSpaceSettingsResponse = z.infer<typeof UpdateSpaceSettingsResponseSchema>
